// API Service for DW Dashboard
// CRITICAL: Use same-origin for API calls to avoid CORS issues

// Always use same-origin for API calls - this works for:
// - preview environments (same domain)
// - production (same domain)
// - localhost dev server proxies /api to backend
let API_BASE_URL;
if (typeof window !== 'undefined') {
  API_BASE_URL = window.location.origin;
} else {
  API_BASE_URL = '';
}

// Remove trailing /api if present to prevent double /api/api issue
API_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

console.log('✅ API_BASE_URL (final):', API_BASE_URL);
console.log('✅ Window host:', typeof window !== 'undefined' ? window.location.host : 'N/A');

// Helper to get auth headers for fetch requests
const getAuthHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const authToken = localStorage.getItem('auth_token');
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return headers;
};

// Authenticated fetch wrapper
const authFetch = async (url, options = {}) => {
  const authToken = localStorage.getItem('auth_token');
  const headers = { ...options.headers };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return fetch(url, { ...options, headers });
};

// Helper function for API calls using XMLHttpRequest to bypass fetch wrappers
export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}/api${endpoint}`;
  
  // Log to confirm we're using XMLHttpRequest
  console.log(`[XMLHttpRequest] ${options.method || 'GET'} ${endpoint}`);
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';
    
    xhr.open(method, url, true);
    
    // Set timeout to 30 seconds (reduced from 60 for faster failure detection)
    xhr.timeout = 30000;
    
    // Set Content-Type only if not already provided in headers
    const hasContentType = options.headers && Object.keys(options.headers).some(
      key => key.toLowerCase() === 'content-type'
    );
    if (!hasContentType) {
      xhr.setRequestHeader('Content-Type', 'application/json');
    }
    
    // MULTI-TENANT AUTH: Automatically add auth token from localStorage
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    }
    
    // Set custom headers
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = function() {
      const responseText = xhr.responseText;
      
      if (xhr.status >= 200 && xhr.status < 300) {
        // Success
        try {
          const data = JSON.parse(responseText);
          resolve(data);
        } catch (e) {
          // If response is not JSON, return the text
          resolve(responseText);
        }
      } else {
        // Error
        let errorMessage = 'API request failed';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = responseText || `HTTP ${xhr.status}: ${xhr.statusText}`;
        }
        reject(new Error(errorMessage));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error occurred'));
    };
    
    xhr.ontimeout = function() {
      reject(new Error('Request timeout'));
    };
    
    // Send request
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

// Helper for FormData uploads (audio files, etc.)
export const apiUpload = async (endpoint, formData) => {
  const url = `${API_BASE_URL}/api${endpoint}`;
  
  console.log(`[XMLHttpRequest-Upload] POST ${endpoint}`);
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.open('POST', url, true);
    // Don't set Content-Type for FormData - browser will set it with boundary
    
    // MULTI-TENANT AUTH: Automatically add auth token from localStorage
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    }
    
    xhr.onload = function() {
      const responseText = xhr.responseText;
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(responseText);
          resolve(data);
        } catch (e) {
          resolve(responseText);
        }
      } else {
        let errorMessage = 'Upload failed';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = responseText || `HTTP ${xhr.status}: ${xhr.statusText}`;
        }
        reject(new Error(errorMessage));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error occurred'));
    };
    
    xhr.ontimeout = function() {
      reject(new Error('Upload timeout'));
    };
    
    xhr.send(formData);
  });
};

// Schedule API
export const scheduleAPI = {
  getAll: (date = null) => {
    const params = date ? `?date=${date}` : '';
    return apiCall(`/schedule${params}`);
  },
  getById: (id) => apiCall(`/schedule/${id}`),
  create: (data) => apiCall('/schedule', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/schedule/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/schedule/${id}`, { method: 'DELETE' }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (read = null) => {
    const params = read !== null ? `?read=${read}` : '';
    return apiCall(`/notifications${params}`);
  },
  create: (data) => apiCall('/notifications', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  markAsRead: (id) => apiCall(`/notifications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ read: true }),
  }),
  markAllAsRead: () => apiCall('/notifications/mark-all-read', { method: 'POST' }),
  delete: (id) => apiCall(`/notifications/${id}`, { method: 'DELETE' }),
};

// Tasks API
export const tasksAPI = {
  getAll: (status = null, category = null) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    const queryString = params.toString();
    return apiCall(`/tasks${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => apiCall(`/tasks/${id}`),
  create: (data) => apiCall('/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/tasks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/tasks/${id}`, { method: 'DELETE' }),
};

// Clients API
export const clientsAPI = {
  getAll: (category = null, status = null) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (status) params.append('status', status);
    const queryString = params.toString();
    return apiCall(`/clients${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => apiCall(`/clients/${id}`),
  create: (data) => apiCall('/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/clients/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/clients/${id}`, { method: 'DELETE' }),
};

// Contacts API
export const contactsAPI = {
  getAll: (category = null) => {
    const params = category ? `?category=${category}` : '';
    return apiCall(`/contacts${params}`);
  },
  getById: (id) => apiCall(`/contacts/${id}`),
  create: (data) => apiCall('/contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/contacts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/contacts/${id}`, { method: 'DELETE' }),
};

// Projects API
export const projectsAPI = {
  getAll: (category = null, status = null) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (status) params.append('status', status);
    const queryString = params.toString();
    return apiCall(`/projects${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => apiCall(`/projects/${id}`),
  create: (data) => apiCall('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/projects/${id}`, { method: 'DELETE' }),
};

// Documents API
export const documentsAPI = {
  getAll: (category = null) => {
    const params = category ? `?category=${category}` : '';
    return apiCall(`/documents${params}`);
  },
  getById: (id) => apiCall(`/documents/${id}`),
  create: (data) => apiCall('/documents', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/documents/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/documents/${id}`, { method: 'DELETE' }),
};

// Messages API
export const messagesAPI = {
  getAll: (folder = null, read = null) => {
    const params = new URLSearchParams();
    if (folder) params.append('folder', folder);
    if (read !== null) params.append('read', read);
    const queryString = params.toString();
    return apiCall(`/messages${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => apiCall(`/messages/${id}`),
  create: (data) => apiCall('/messages', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  markAsRead: (id) => apiCall(`/messages/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ read: true }),
  }),
  star: (id, starred) => apiCall(`/messages/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ starred }),
  }),
  moveFolder: (id, folder) => apiCall(`/messages/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ folder }),
  }),
  delete: (id) => apiCall(`/messages/${id}`, { method: 'DELETE' }),
  sendSMS: (data) => apiCall('/messages/send-sms', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Conversations API
export const conversationsAPI = {
  getAll: (folder = null, category = null) => {
    const params = new URLSearchParams();
    if (folder) params.append('folder', folder);
    if (category) params.append('category', category);
    const queryString = params.toString();
    return apiCall(`/conversations${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => apiCall(`/conversations/${id}`),
  create: (data) => apiCall('/conversations', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/conversations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/conversations/${id}`, { method: 'DELETE' }),
  addMessage: (id, message) => apiCall(`/conversations/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify(message),
  }),
  markAsRead: (id) => apiCall(`/conversations/${id}/mark-read`, {
    method: 'POST',
  }),
  regenerateDraft: (id) => apiCall(`/webhooks/messages/regenerate-draft/${id}`, {
    method: 'POST',
  }),
  addReaction: (conversationId, messageId, reaction) => apiCall(`/conversations/${conversationId}/messages/${messageId}/reaction?reaction=${reaction}`, {
    method: 'PATCH',
  }),
};

// Scheduled Messages API
export const scheduledMessagesAPI = {
  getAll: (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/scheduled-messages${params}`);
  },
  getById: (id) => apiCall(`/scheduled-messages/${id}`),
  create: (data) => apiCall('/scheduled-messages', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/scheduled-messages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  cancel: (id) => apiCall(`/scheduled-messages/${id}`, {
    method: 'DELETE',
  }),
  delete: (id) => apiCall(`/scheduled-messages/${id}`, {
    method: 'DELETE',
  }),
  sendPending: () => apiCall('/scheduled-messages/send-pending', {
    method: 'POST',
  }),
};

// KPIs API
export const kpisAPI = {
  getAll: () => apiCall('/kpis'),
  getByName: (name) => apiCall(`/kpis/${name}`),
  create: (data) => apiCall('/kpis', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/kpis/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/kpis/${id}`, { method: 'DELETE' }),
};

// Travel API
export const travelAPI = {
  getAll: (status = null, userId = null) => {
    const params = [];
    if (status) params.push(`status=${status}`);
    if (userId) params.push(`user_id=${encodeURIComponent(userId)}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return apiCall(`/travel${queryString}`);
  },
  getById: (id) => apiCall(`/travel/${id}`),
  create: (data, userId = null) => {
    const params = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return apiCall(`/travel${params}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: (id, data) => apiCall(`/travel/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/travel/${id}`, { method: 'DELETE' }),
};

// Speaking Events API
export const speakingEventsAPI = {
  getAll: (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/speaking-events${params}`);
  },
  getById: (id) => apiCall(`/speaking-events/${id}`),
  create: (data) => apiCall('/speaking-events', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/speaking-events/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/speaking-events/${id}`, { method: 'DELETE' }),
};

// Media API
export const mediaAPI = {
  getAll: (category = null, type = null) => {
    let url = '/media';
    const params = [];
    if (category) params.push(`category=${category}`);
    if (type) params.push(`type=${type}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return apiCall(url);
  },
  
  create: (data) => apiCall('/media', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiCall(`/media/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiCall(`/media/${id}`, {
    method: 'DELETE',
  }),

  // Upload media file
  uploadMedia: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const headers = {};
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/api/uploads/media`, {
      method: 'POST',
      headers,
      body: formData, // Don't set Content-Type, let browser set it with boundary
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
    
    return await response.json();
  },

  // Upload thumbnail using XMLHttpRequest to bypass fetch wrappers
  uploadThumbnail: async (file) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);
      
      xhr.open('POST', `${API_BASE_URL}/api/uploads/thumbnail`, true);
      
      // MULTI-TENANT AUTH: Add auth token
      const authToken = localStorage.getItem('auth_token');
      if (authToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            reject(new Error('Failed to parse upload response'));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Upload failed: Network error'));
      };
      
      xhr.send(formData);
    });
  },
};

// Content Posts API
export const contentPostsAPI = {
  getAll: (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/content-posts${params}`);
  },
  getById: (id) => apiCall(`/content-posts/${id}`),
  create: (data) => apiCall('/content-posts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/content-posts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/content-posts/${id}`, { method: 'DELETE' }),
};

// Listings API
export const listingsAPI = {
  getAll: (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/listings${params}`);
  },
  getById: (id) => apiCall(`/listings/${id}`),
  create: (data) => apiCall('/listings', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/listings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/listings/${id}`, { method: 'DELETE' }),
};

// Leads API
export const leadsAPI = {
  getAll: (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/leads${params}`);
  },
  getById: (id) => apiCall(`/leads/${id}`),
  create: (data) => apiCall('/leads', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/leads/${id}`, { method: 'DELETE' }),
};

// Outlook API - REMOVED (use Nylas instead)
export const outlookAPI = {};

// Chat API
export const chatAPI = {
  query: (message, sessionId = null, provider = 'openai', history = []) => apiCall('/chat/query', {
    method: 'POST',
    body: JSON.stringify({
      message,
      session_id: sessionId,
      provider,
      history
    }),
  }),
  
  // Streaming query - returns chunks as they arrive
  queryStream: async (message, sessionId = null, provider = 'openai', history = [], onChunk, onComplete, onError) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/chat/query-stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message,
          session_id: sessionId,
          provider,
          history
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sessionInfo = null;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            
            if (data.error) {
              onError(data.error);
              return;
            }
            
            if (data.session_id) {
              sessionInfo = { session_id: data.session_id, provider: data.provider };
            }
            
            if (data.chunk) {
              onChunk(data.chunk);
            }
            
            if (data.done) {
              onComplete(data.full_text, sessionInfo);
              return;
            }
          }
        }
      }
    } catch (error) {
      onError(error.message);
    }
  },
  
  getHistory: (sessionId) => apiCall(`/chat/history/${sessionId}`),
  getDailyGreeting: () => apiCall('/chat/daily-greeting'),
};

// Outlook Calendar Auth API - REMOVED (use Nylas instead)
export const outlookCalendarAuthAPI = {};

// Outlook2 Email Auth API - REMOVED (use Nylas instead)
export const outlook2AuthAPI = {};

// Outlook2 Email API - REMOVED (use Nylas instead)
export const outlook2EmailAPI = {};

// AI Email Features API
export const aiEmailAPI = {
  // Categorize a single email using AI
  categorizeEmail: (emailData) => apiCall('/ai-email/categorize', {
    method: 'POST',
    body: JSON.stringify(emailData),
  }),
  
  // Categorize multiple emails in bulk
  categorizeBulk: (emails) => apiCall('/ai-email/categorize-bulk', {
    method: 'POST',
    body: JSON.stringify({ emails }),
  }),
  
  // Generate AI reply to an email
  generateEmailReply: (emailData) => apiCall('/ai-email/generate-reply', {
    method: 'POST',
    body: JSON.stringify(emailData),
  }),
  
  // Generate AI reply to a text message
  generateMessageReply: (messageData) => apiCall('/ai-email/generate-message-reply', {
    method: 'POST',
    body: JSON.stringify(messageData),
  }),
};

// Outlook Calendar API - REMOVED (use Nylas instead)
export const outlookCalendarAPI = {};

// Outlook Contacts API - REMOVED (use Nylas instead)
export const outlookContactsAPI = {};

// Outlook Email API - REMOVED (use Nylas instead)
export const outlookEmailAPI = {};

// Scheduled Emails API
export const scheduledEmailsAPI = {
  create: (emailData) => apiCall('/scheduled-emails/', {
    method: 'POST',
    body: JSON.stringify(emailData),
  }),
  getAll: (status = null) => {
    const query = status ? `?status=${status}` : '';
    return apiCall(`/scheduled-emails/${query}`);
  },
  getById: (id) => apiCall(`/scheduled-emails/${id}`),
  update: (id, emailData) => apiCall(`/scheduled-emails/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(emailData),
  }),
  cancel: (id) => apiCall(`/scheduled-emails/${id}`, {
    method: 'DELETE',
  }),
  sendNow: (id) => apiCall(`/scheduled-emails/${id}/send`, {
    method: 'POST',
  }),
  processDue: () => apiCall('/scheduled-emails/process-due', {
    method: 'POST',
  }),
};

// LinkedIn Scraper API
export const linkedInAPI = {
  scrapeProfile: (profileUrl) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/api/linkedin/scrape/profile`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(xhr.responseText || 'Failed to scrape LinkedIn profile'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error occurred'));
      };
      
      xhr.send(JSON.stringify({ profile_url: profileUrl }));
    });
  },

  searchProfile: (name, company = null) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/api/linkedin/search/profile`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(xhr.responseText || 'Failed to search LinkedIn profiles'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error occurred'));
      };
      
      xhr.send(JSON.stringify({ name, company }));
    });
  },
};



// ElevenLabs Text-to-Speech API
export const elevenlabsAPI = {
  // Convert text to speech using ElevenLabs
  speak: async (text, voiceId = '21m00Tcm4TlvDq8ikWAM') => {  // Rachel - clear enunciation
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${API_BASE_URL}/api/elevenlabs/speak`;
      
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.responseType = 'blob'; // Important: we're receiving audio
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Create audio URL from blob
          const audioBlob = xhr.response;
          const audioUrl = URL.createObjectURL(audioBlob);
          resolve(audioUrl);
        } else {
          reject(new Error(`Failed to generate speech: ${xhr.status}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error occurred'));
      };
      
      xhr.send(JSON.stringify({ text, voice_id: voiceId }));
    });
  },
  
  // Get available voices
  getVoices: () => apiCall('/elevenlabs/voices'),
};


// OpenAI Whisper Speech-to-Text API
export const whisperAPI = {
  // Transcribe audio using Whisper
  transcribe: async (audioBlob) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${API_BASE_URL}/api/whisper/transcribe`;
      
      xhr.open('POST', url, true);
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          reject(new Error(`Failed to transcribe: ${xhr.status}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error occurred'));
      };
      
      // Create FormData with audio file
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      xhr.send(formData);
    });
  },
};


// Gmail API - Multi-account support
export const gmailAPI = {
  // Get Gmail connection status for specific account or all accounts
  getStatus: async (accountId = null) => {
    const params = accountId ? `?account_id=${accountId}` : '';
    const response = await authFetch(`${API_BASE_URL}/api/gmail/status${params}`);
    if (!response.ok) throw new Error('Failed to check Gmail status');
    return response.json();
  },
  
  // Get all accounts with connection status
  getAccounts: async () => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/accounts`);
    if (!response.ok) throw new Error('Failed to get accounts');
    return response.json();
  },
  
  // Get Gmail auth URL for specific account
  getAuthUrl: async (accountId = 'creatrhq') => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/auth/url?account_id=${accountId}`);
    if (!response.ok) throw new Error('Failed to get Gmail auth URL');
    return response.json();
  },
  
  // Disconnect Gmail account
  disconnect: async (accountId = 'creatrhq') => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/disconnect?account_id=${accountId}`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to disconnect Gmail');
    return response.json();
  },
  
  // Get Gmail messages for specific account
  getMessages: async (maxResults = 50, pageToken = null, labelId = null, accountId = 'creatrhq') => {
    const params = new URLSearchParams({ max_results: maxResults, account_id: accountId });
    if (pageToken) params.append('page_token', pageToken);
    if (labelId) params.append('label_id', labelId);
    
    const response = await authFetch(`${API_BASE_URL}/api/gmail/messages?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Gmail messages');
    return response.json();
  },
  
  // Get single Gmail message details
  getMessage: async (messageId, accountId = 'creatrhq') => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/messages/${messageId}?account_id=${accountId}`);
    if (!response.ok) throw new Error('Failed to fetch Gmail message');
    return response.json();
  },
  
  // Send Gmail message from specific account
  send: async (emailData, accountId = 'creatrhq') => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ...emailData, account_id: accountId })
    });
    if (!response.ok) throw new Error('Failed to send Gmail message');
    return response.json();
  },
  
  // Mark as read/unread
  markAsRead: async (messageId, isRead = true, accountId = 'creatrhq') => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/messages/${messageId}/read?is_read=${isRead}&account_id=${accountId}`, {
      method: 'PATCH'
    });
    if (!response.ok) throw new Error('Failed to mark as read');
    return response.json();
  },
  
  // Delete message
  delete: async (messageId, accountId = 'creatrhq') => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/messages/${messageId}?account_id=${accountId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete message');
    return response.json();
  },
  
  // Star/unstar message
  starMessage: async (messageId, isStarred = true, accountId = 'creatrhq') => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/messages/${messageId}/star?is_starred=${isStarred}&account_id=${accountId}`, {
      method: 'PATCH'
    });
    if (!response.ok) throw new Error('Failed to star message');
    return response.json();
  }
};

// Gmail Contacts API
export const gmailContactsAPI = {
  // Get Gmail contacts
  getAll: async () => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/contacts`);
    if (!response.ok) throw new Error('Failed to fetch Gmail contacts');
    const data = await response.json();
    return data.contacts || [];
  }
};

// Gmail Calendar API
export const gmailCalendarAPI = {
  // Get Gmail calendar events
  getEvents: async (timeMin = null, timeMax = null, maxResults = 250) => {
    const params = new URLSearchParams({ max_results: maxResults });
    if (timeMin) params.append('time_min', timeMin);
    if (timeMax) params.append('time_max', timeMax);
    
    const response = await authFetch(`${API_BASE_URL}/api/gmail/events?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Gmail calendar events');
    const data = await response.json();
    return data.events || [];
  },
  
  // Get Gmail calendars list
  getCalendars: async () => {
    const response = await authFetch(`${API_BASE_URL}/api/gmail/calendars`);
    if (!response.ok) throw new Error('Failed to fetch Gmail calendars');
    const data = await response.json();
    return data.calendars || [];
  }
};

// Personal Phone API
export const personalPhoneAPI = {
  // Get all personal phone conversations
  getConversations: async () => {
    return apiCall('/personal-phone/conversations');
  },
  
  // Test webhook endpoint
  testWebhook: async () => {
    return apiCall('/personal-phone/test', { method: 'POST' });
  },
  
  // Queue message to be sent via iPhone
  queueMessage: async (recipientPhone, recipientName, message, conversationId) => {
    return apiCall('/personal-phone/queue', {
      method: 'POST',
      body: JSON.stringify({
        recipient_phone: recipientPhone,
        recipient_name: recipientName,
        message: message,
        conversation_id: conversationId
      })
    });
  },
  
  // Get pending messages (for iPhone to fetch)
  getPending: async () => {
    return apiCall('/personal-phone/pending');
  },
  
  // Mark message as sent (iPhone calls this after sending)
  markSent: async (messageId) => {
    return apiCall(`/personal-phone/mark-sent/${messageId}`, {
      method: 'POST'
    });
  },
  
  // Mark message as failed
  markFailed: async (messageId) => {
    return apiCall(`/personal-phone/mark-failed/${messageId}`, {
      method: 'POST'
    });
  },
  
  // Delete personal phone conversation
  deleteConversation: async (conversationId) => {
    return apiCall(`/personal-phone/conversation/${conversationId}`, {
      method: 'DELETE'
    });
  },
  
  // Mark conversation as read
  markAsRead: async (conversationId) => {
    return apiCall(`/personal-phone/conversation/${conversationId}/mark-read`, {
      method: 'POST'
    });
  }
};





// Notes API
export const notesAPI = {
  // Get all notes
  getAll: async (search = null, tag = null) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (tag) params.append('tag', tag);
    
    const query = params.toString() ? `?${params}` : '';
    return apiCall(`/notes${query}`);
  },
  
  // Get single note by ID
  get: async (noteId) => {
    return apiCall(`/notes/${noteId}`);
  },
  
  // Create new note
  create: async (noteData) => {
    return apiCall('/notes', {
      method: 'POST',
      body: JSON.stringify(noteData)
    });
  },
  
  // Update note
  update: async (noteId, updates) => {
    return apiCall(`/notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  },
  
  // Delete note
  delete: async (noteId) => {
    return apiCall(`/notes/${noteId}`, {
      method: 'DELETE'
    });
  }
};



// Outlook2 Contacts API - REMOVED (use Nylas instead)
export const outlook2ContactsAPI = {};

// Outlook2 Calendar API - REMOVED (use Nylas instead)
export const outlook2CalendarAPI = {};

// iCloud Email API
export const icloudEmailAPI = {
  // Send new email
  sendEmail: (data) => apiCall('/icloud-email/send', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Reply to email - userId is required, no default
  replyToEmail: (emailId, body, replyAll = false, userId) => apiCall('/icloud-email/reply', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      email_id: emailId,
      body: body,
      reply_all: replyAll
    }),
  }),
  
  // Reply with attachments - userId is required, no default
  replyWithAttachments: (emailId, body, replyAll = false, cc = [], bcc = [], attachments = [], userId) => apiCall('/icloud-email/reply', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      email_id: emailId,
      body: body,
      reply_all: replyAll,
      cc: cc,
      bcc: bcc,
      attachments: attachments
    }),
  }),
  
  // List emails
  listEmails: (userId, skip = 0, limit = 20, filterPromotional = true) => {
    const params = new URLSearchParams();
    params.append('user_id', userId);
    params.append('skip', skip);
    params.append('limit', limit);
    params.append('filter_promotional', filterPromotional);
    return apiCall(`/icloud-email/list?${params}`);
  },
  
  // Sync emails
  syncEmails: (userId) => apiCall('/icloud-email/sync', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  }),
  
  // Mark email as read/unread
  markEmailRead: (emailId, userId, isRead) => apiCall(`/icloud-email/mark-read/${emailId}?user_id=${userId}&is_read=${isRead}`, {
    method: 'PUT',
  }),
  
  // Delete email (move to trash) - userId is required
  deleteEmail: (emailId, userId) => apiCall(`/icloud-email/emails/${emailId}?user_id=${userId}`, {
    method: 'DELETE',
  }),
  
  // Flag/unflag email - userId is required
  flagEmail: (emailId, userId, isFlagged = true) => apiCall(`/icloud-email/emails/${emailId}/flag?user_id=${userId}&is_flagged=${isFlagged}`, {
    method: 'PATCH',
  }),
};

// iCloud Reminders API
export const icloudRemindersAPI = {
  // Get reminders configuration status
  getConfig: (userId) => apiCall(`/icloud-reminders/config/${userId}`),
  
  // Configure iCloud reminders
  configure: (userId, icloudEmail, appPassword) => apiCall('/icloud-reminders/config', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, icloud_email: icloudEmail, app_password: appPassword }),
  }),
  
  // Get reminders
  getReminders: (userId) => apiCall(`/icloud-reminders/reminders?user_id=${userId}`),
  
  // Delete configuration
  deleteConfig: (userId) => apiCall(`/icloud-reminders/config/${userId}`, {
    method: 'DELETE',
  }),
};

// iCloud Calendar API
export const icloudCalendarAPI = {
  // Get calendar configuration status
  getConfig: (userId) => apiCall(`/icloud-calendar/config/${userId}`),
  
  // Configure iCloud calendar
  configure: (userId, appleId, appPassword) => apiCall('/icloud-calendar/config', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, apple_id: appleId, app_password: appPassword }),
  }),
  
  // Sync calendar events
  syncEvents: (userId) => apiCall('/icloud-calendar/sync', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  }),
  
  // Get calendar events
  getEvents: (userId, startDate = null, endDate = null) => {
    let url = `/icloud-calendar/events?user_id=${userId}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return apiCall(url);
  },
  
  // Create calendar event
  createEvent: (userId, eventData) => apiCall(`/icloud-calendar/events?user_id=${userId}`, {
    method: 'POST',
    body: JSON.stringify(eventData),
  }),
  
  // Delete configuration
  deleteConfig: (userId) => apiCall(`/icloud-calendar/config/${userId}`, {
    method: 'DELETE',
  }),
};

// iCloud Photos API
export const icloudPhotosAPI = {
  // Sync photos
  syncPhotos: (userId) => apiCall(`/icloud-photos/sync?user_id=${userId}`, {
    method: 'POST',
  }),
  
  // Get sync status
  getSyncStatus: (userId) => apiCall(`/icloud-photos/sync-status?user_id=${userId}`),
  
  // List photos
  listPhotos: (userId, skip = 0, limit = 50, assetType = null) => {
    const params = `user_id=${userId}&skip=${skip}&limit=${limit}${assetType ? `&asset_type=${assetType}` : ''}`;
    return apiCall(`/icloud-photos/list?${params}`);
  },
  
  // Search photos
  searchPhotos: (query, userId, assetType = null) => {
    const params = `query=${encodeURIComponent(query)}&user_id=${userId}${assetType ? `&asset_type=${assetType}` : ''}`;
    return apiCall(`/icloud-photos/search?${params}`);
  },
  
  // Delete photo
  deletePhoto: (icloudId, userId) => apiCall(`/icloud-photos/${icloudId}?user_id=${userId}`, {
    method: 'DELETE',
  }),
};

// iCloud Contacts API
export const icloudContactsAPI = {
  // Configure iCloud for contacts
  configure: (userId, icloudEmail, appPassword) => apiCall('/icloud-email/config', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, icloud_email: icloudEmail, app_password: appPassword }),
  }),
  
  // Sync contacts from iCloud
  syncContacts: (userId) => apiCall(`/icloud-contacts/sync?user_id=${userId}`, {
    method: 'POST',
  }),
  
  // Get contacts list
  getContacts: (userId, skip = 0, limit = 5000) => 
    apiCall(`/icloud-contacts/list?user_id=${userId}&skip=${skip}&limit=${limit}`),
  
  // Get sync status
  getSyncStatus: (userId) => apiCall(`/icloud-contacts/sync-status?user_id=${userId}`),
  
  // Clear all iCloud contacts
  clearContacts: (userId) => apiCall(`/icloud-contacts/clear?user_id=${userId}`, {
    method: 'DELETE',
  }),
};

export const pushcutAPI = {
  sendText: (contact, message, pushcutSecret, notificationName = 'Send Dashboard Text') => apiCall('/pushcut/send-text', {
    method: 'POST',
    body: JSON.stringify({ contact, message, pushcut_secret: pushcutSecret, notification_name: notificationName }),
  }),
  test: () => apiCall('/pushcut/test'),
};

export const macMessagesAPI = {
  sendMessage: (contact, message) => apiCall('/mac-messages/send', {
    method: 'POST',
    body: JSON.stringify({ contact, message }),
  }),
};

// Mac Messages Bridge API (for syncing with Mac)
export const macMessagesBridgeAPI = {
  // Get bridge status
  getStatus: () => apiCall('/mac-messages-bridge/status'),
  
  // Get synced conversations
  getConversations: (limit = 50) => apiCall(`/mac-messages-bridge/conversations?limit=${limit}`),
  
  // Get messages for a conversation
  getMessages: (chatId, limit = 100) => apiCall(`/mac-messages-bridge/messages/${encodeURIComponent(chatId)}?limit=${limit}`),
  
  // Send a message (queued for Mac bridge)
  sendMessage: (recipient, message) => apiCall('/mac-messages-bridge/send', {
    method: 'POST',
    body: JSON.stringify({ recipient, message }),
  }),
  
  // Clear all synced data
  clearData: () => apiCall('/mac-messages-bridge/clear', { method: 'DELETE' }),
};

// Cresa Email API (synced from Mac Outlook)
export const cresaEmailAPI = {
  // Get emails
  getEmails: (folder = 'inbox', limit = 50, skip = 0, unreadOnly = false) => {
    const params = new URLSearchParams();
    params.append('folder', folder);
    params.append('limit', limit);
    params.append('skip', skip);
    if (unreadOnly) params.append('unread_only', 'true');
    return apiCall(`/cresa-email/messages?${params}`);
  },
  
  // Get single email
  getEmail: (emailId) => apiCall(`/cresa-email/messages/${emailId}`),
  
  // Get stats
  getStats: () => apiCall('/cresa-email/stats'),
  
  // Mark email as read/unread
  markRead: (emailId, isRead = true) => apiCall(`/cresa-email/messages/${emailId}/read?is_read=${isRead}`, {
    method: 'PATCH',
  }),
  
  // Flag/unflag email
  flagEmail: (emailId, isFlagged = true) => apiCall(`/cresa-email/messages/${emailId}/flag?is_flagged=${isFlagged}`, {
    method: 'PATCH',
  }),
  
  // Delete email
  deleteEmail: (emailId) => apiCall(`/cresa-email/messages/${emailId}`, {
    method: 'DELETE',
  }),
  
  // Queue email to send via Mac
  sendEmail: (data) => apiCall('/cresa-email/send-via-mac', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};



// Gmail IMAP API (App Password based - no OAuth)
export const gmailImapAPI = {
  // Connect account with app password
  connect: (accountId, email, appPassword) => apiCall('/gmail-imap/connect', {
    method: 'POST',
    body: JSON.stringify({ account_id: accountId, email, app_password: appPassword }),
  }),
  
  // Get connection status
  getStatus: (accountId) => apiCall(`/gmail-imap/status/${accountId}`),
  
  // Fetch emails
  getEmails: (accountId, folder = 'inbox', limit = 50) => 
    apiCall(`/gmail-imap/emails/${accountId}?folder=${folder}&limit=${limit}`),
  
  // Disconnect account
  disconnect: (accountId) => apiCall(`/gmail-imap/disconnect/${accountId}`, {
    method: 'DELETE',
  }),
  
  // Test endpoint
  test: () => apiCall('/gmail-imap/test'),
};

// Cresa Calendar API (synced from Mac Outlook)
export const cresaCalendarAPI = {
  // Get calendar events
  getEvents: (startDate, endDate, limit = 200) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('limit', limit);
    return apiCall(`/cresa-calendar/events?${params}`);
  },
  
  // Get single event
  getEvent: (eventId) => apiCall(`/cresa-calendar/events/${eventId}`),
  
  // Get stats
  getStats: () => apiCall('/cresa-calendar/stats'),
  
  // Get today's events
  getTodayEvents: () => apiCall('/cresa-calendar/today'),
  
  // Delete event
  deleteEvent: (eventId) => apiCall(`/cresa-calendar/events/${eventId}`, {
    method: 'DELETE',
  }),
  
  // Sync calendar events to meetings page
  syncToMeetings: () => apiCall('/cresa-calendar/sync-to-meetings', {
    method: 'POST',
  }),
};


// Meetings API
export const meetingsAPI = {
  getAll: (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/meetings${params}`);
  },
  
  get: (meetingId) => apiCall(`/meetings/${meetingId}`),
  
  create: (meetingData) => apiCall('/meetings', {
    method: 'POST',
    body: JSON.stringify(meetingData),
  }),
  
  update: (meetingId, updates) => apiCall(`/meetings/${meetingId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  
  delete: (meetingId) => apiCall(`/meetings/${meetingId}`, {
    method: 'DELETE',
  }),
};

// Calls API - for Twilio phone calls
export const callsAPI = {
  // Get all calls
  getAll: (direction = null, status = null, limit = 100) => {
    const params = new URLSearchParams();
    if (direction) params.append('direction', direction);
    if (status) params.append('status', status);
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return apiCall(`/calls${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get single call
  get: (callId) => apiCall(`/calls/${callId}`),
  
  // Make outbound call
  makeCall: (toPhone, recipientName = null, contactId = null) => apiCall('/calls/make-call', {
    method: 'POST',
    body: JSON.stringify({
      to_phone: toPhone,
      recipient_name: recipientName,
      contact_id: contactId,
    }),
  }),
  
  // End call
  endCall: (callSid) => apiCall(`/calls/${callSid}/end`, {
    method: 'POST',
  }),
  
  // Update call outcome
  updateOutcome: (callId, outcome, notes = null, followUpDate = null) => apiCall(`/calls/${callId}/outcome`, {
    method: 'PUT',
    body: JSON.stringify({
      outcome,
      notes,
      follow_up_date: followUpDate,
    }),
  }),
};

